import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  loadConversation,
  saveConversation,
  clearConversation,
  type StoredMessage,
  type StoredState,
} from './storage';
import { track } from './analytics';

type ChatOptions = { bookingUrl: string };

const CHAT_ENDPOINT = '/api/chat';
const MAX_INPUT_LEN = 4000;
const ASSISTANT_CTA_EVERY = 3;
// Logo lives at site path /logo.svg. Absolute URL because Hosting cleanUrls
// collapses /chat/ to /chat - relative refs resolve from the wrong base when
// the URL has no trailing slash.
const LOGO_PATH = '/logo.svg';

const GREETING =
  "Hi - I'm BMEC's AI. Tell me a bit about you and what you're working on. Please keep IP-sensitive details out of this chat - if you want to know how to discuss an idea before an NDA, just ask. For real project conversations, the reliable next step is to book an intro call.";

// Sample starter prompts shown as typewriter-animated chips when the chat
// is empty. Targeted at BMEC's actual prospect categories from positioning.md.
// Pool is intentionally large so successive rotations stay fresh.
const STARTER_PROMPTS = [
  "I've got an idea but I don't know where to start",
  "I have a sketch on a napkin and need help making it real",
  "I need to know if my idea is technically feasible",
  "I'm a founder looking for a long-term engineering partner",
  "I have an MVP and need to plan the next version",
  "I have a prototype that can't yet be manufactured",
  "My prototype works on the bench but fails in the field",
  "I need to harden a lab prototype for production",
  "I'm scaling from 3D printing to injection moulding",
  "I have a working concept and need a pre-clinical version",
  "My vendor is letting me down and I need help",
  "We're months behind schedule with our current vendor",
  "I need a second opinion on my current vendor's work",
  "I think I need to switch engineering partners",
  "We were sold something that didn't work, need to start again",
  "We've spent a lot on engineering with nothing to show for it",
  "I'm a non-technical founder looking for fractional CTO support",
  "I need senior technical leadership without a full-time hire",
  "I want a CTO-level voice when I talk to investors",
  "I need help translating between business and engineering",
  "I need technical due diligence on a vendor before I sign",
  "I need help getting a medical device through certification",
  "I have a medical device idea and need a regulatory pathway",
  "I need to build a quality system for medical device development",
  "My medical device prototype needs clinical validation help",
  "I need to file an IDE for a medical device",
  "I want to build a mobile app and a connected device",
  "I need a web platform and a mobile app to match",
  "I need an API and a dashboard for my IoT fleet",
  "I need to rebuild a web app that's been outgrown",
  "I need someone to take over a stalled mobile app",
  "I need a bespoke piece of equipment built from scratch",
  "I need help with PCB layout and EMC",
  "I have a mechanical design that won't pass certification",
  "I need help with industrial design and DFM",
  "I need a custom enclosure for an electronics project",
  "I have a circuit that needs a production-ready design",
  "I want to add IoT to an existing product line",
  "I need to automate a manual production process",
  "I have a fleet of devices and need a cloud backend",
  "I want condition monitoring on civil infrastructure",
  "I need a mechatronic system for a niche application",
  "I have a patent idea and need engineering to back it up",
  "I need to file a provisional patent with engineering input",
  "I want to know if my idea is patentable before investing more",
  "I want to add AI features to my product",
  "I need an AI assistant that joins my team meetings",
  "I want to automate document processing with AI",
  "I need help figuring out where AI fits in my business",
  "I want to know if BMEC can help with my project",
  "I need a discovery phase to scope a real project",
  "I just want to chat about what's possible",
  "I need a quick technical opinion on something",
  "I want to understand what BMEC's process looks like",
  "I'm a designer who needs engineering rigour to take this further",
  "I need to make sense of an existing codebase before extending it",
  "I want a fixed-cost discovery phase before a bigger commitment",
];

function pickStarters(n: number): string[] {
  const pool = [...STARTER_PROMPTS];
  const out: string[] = [];
  while (out.length < n && pool.length > 0) {
    const idx = Math.floor(Math.random() * pool.length);
    out.push(pool.splice(idx, 1)[0]!);
  }
  return out;
}

// Render assistant markdown to safe HTML. Inline-friendly (paragraphs,
// bold/italic, lists, links) but no raw HTML, no scripts.
function renderMarkdown(md: string): string {
  marked.setOptions({ breaks: true, gfm: true });
  const raw = marked.parse(md, { async: false }) as string;
  return DOMPurify.sanitize(raw, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'b', 'i', 'a', 'ul', 'ol', 'li',
      'code', 'pre', 'blockquote', 'h3', 'h4', 'h5',
    ],
    ALLOWED_ATTR: ['href', 'rel', 'target'],
    ALLOW_DATA_ATTR: false,
  });
}

// Open in a new tab and DO NOT navigate the host frame. With `noopener`,
// window.open returns null even on success, so we cannot reliably detect
// a blocked popup. That's fine - browsers show their own blocked-popup UI,
// and middle-click still works as a user fallback.
function openExternal(url: string): void {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function initChat(root: HTMLElement, opts: ChatOptions): void {
  const initial: StoredState = loadConversation();
  const state: StoredState & { bookingUrl: string; busy: boolean } = {
    conversationId: initial.conversationId,
    messages: initial.messages,
    bookingUrl: opts.bookingUrl,
    busy: false,
  };

  track('chat_loaded', {
    has_history: state.messages.length > 0,
    conversation_id: state.conversationId ?? 'new',
  });

  function persist() {
    saveConversation({ conversationId: state.conversationId, messages: state.messages });
  }

  function render() {
    root.innerHTML = `
      <div class="chat-shell">
        <div class="chat-banner">
          AI can make mistakes, so treat this as a first pass - and please keep confidential or IP-sensitive details out of the chat. For real project discussions, <a class="banner-cta" href="${state.bookingUrl}">book an intro call</a>.
        </div>
        <header class="chat-header">
          <a class="brand" href="https://bmec.co">
            <img src="${LOGO_PATH}" class="brand-logo" alt="BMEC" />
          </a>
          <div class="actions">
            <button class="btn-link" data-action="clear" type="button">Clear</button>
            <a class="btn-cta" href="${state.bookingUrl}">Book intro call &rarr;</a>
          </div>
        </header>
        <div class="chat-messages" id="messages"></div>
        <form class="chat-input" id="form">
          <textarea id="input" rows="2" maxlength="${MAX_INPUT_LEN}" placeholder="Tell me a bit about you and what you're working on..."></textarea>
          <button class="btn-send" type="submit" id="send">Send</button>
        </form>
      </div>
    `;
    renderMessages();
    bindEvents();
    focusInput();
  }

  function renderMessages() {
    const el = document.getElementById('messages');
    if (!el) return;
    if (state.messages.length === 0) {
      el.innerHTML = `
        <div class="msg msg-assistant">
          <div class="msg-bubble">${renderMarkdown(GREETING)}</div>
        </div>
        <div class="starters" id="starters"></div>
      `;
      starterRotation.active = true;
      starterRotation.round = 0;
      starterRotation.recent.clear();
      renderStarterChips(pickStartersFresh(4));
      return;
    }
    let assistantCount = 0;
    const html = state.messages
      .map((m) => {
        const body =
          m.role === 'assistant' ? renderMarkdown(m.content) : `<p>${escapeHtml(m.content)}</p>`;
        let block = `<div class="msg msg-${m.role}"><div class="msg-bubble">${body}</div></div>`;
        if (m.role === 'assistant') {
          assistantCount++;
          if (assistantCount % ASSISTANT_CTA_EVERY === 0) {
            block += `<div class="msg msg-cta"><a href="${state.bookingUrl}">Want to talk to a human? Book a 15-minute intro call &rarr;</a></div>`;
          }
        }
        return block;
      })
      .join('');
    el.innerHTML = html;
    el.scrollTop = el.scrollHeight;
  }

  // Rotation state for starter chips - keeps cycling new prompts in until
  // the user interacts (focus, type, click chip, send message).
  const starterRotation = {
    active: true,
    timeoutId: 0 as ReturnType<typeof setTimeout> | 0,
    recent: new Set<string>(),
    round: 0,
  };

  function stopStarterRotation() {
    starterRotation.active = false;
    if (starterRotation.timeoutId) clearTimeout(starterRotation.timeoutId);
  }

  function pickStartersFresh(n: number): string[] {
    // Avoid recently-shown prompts. If the recent set covers everything
    // available, reset it and start a new cycle.
    if (starterRotation.recent.size >= STARTER_PROMPTS.length - 1) {
      starterRotation.recent.clear();
    }
    const pool = STARTER_PROMPTS.filter((s) => !starterRotation.recent.has(s));
    const out: string[] = [];
    while (out.length < n && pool.length > 0) {
      const idx = Math.floor(Math.random() * pool.length);
      const picked = pool.splice(idx, 1)[0]!;
      out.push(picked);
      starterRotation.recent.add(picked);
    }
    return out;
  }

  function renderStarterChips(prompts: string[]) {
    const container = document.getElementById('starters');
    if (!container) return;
    container.innerHTML = prompts
      .map(
        (_s, i) =>
          `<button class="starter-chip" data-i="${i}" type="button"><span class="starter-text"></span><span class="starter-cursor"></span></button>`,
      )
      .join('');
    animateStarters(prompts);
  }

  function animateStarters(prompts: string[]) {
    const container = document.getElementById('starters');
    if (!container) return;
    const PER_CHAR = 18; // ms
    const STAGGER = 220; // ms between chips starting
    let lastFinishAt = 0;
    prompts.forEach((text, i) => {
      const chip = container.querySelector(`[data-i="${i}"]`) as HTMLButtonElement | null;
      if (!chip) return;
      const textEl = chip.querySelector('.starter-text') as HTMLElement | null;
      if (!textEl) return;
      chip.classList.add('typing');
      const startAt = i * STAGGER;
      let charIndex = 0;
      const estTypeMs = (text.length + 1) * (PER_CHAR + 4);
      lastFinishAt = Math.max(lastFinishAt, startAt + estTypeMs);
      setTimeout(() => {
        if (!starterRotation.active) return;
        chip.classList.add('visible');
        const tick = () => {
          if (!starterRotation.active) return;
          if (charIndex > text.length) {
            chip.classList.remove('typing');
            return;
          }
          textEl.textContent = text.slice(0, charIndex);
          charIndex++;
          setTimeout(tick, PER_CHAR + Math.random() * 8);
        };
        tick();
      }, startAt);
      chip.addEventListener('click', () => {
        const inputEl = document.getElementById('input') as HTMLTextAreaElement | null;
        if (!inputEl) return;
        inputEl.value = text;
        inputEl.focus();
        stopStarterRotation();
        track('chat_starter_used', { index: i, round: starterRotation.round, text_length: text.length });
      });
    });

    // Schedule next rotation - hold 20s after the last chip finishes typing
    // before fading out the next batch. Long enough for a user to actually
    // read all four prompts. Cancelled by stopStarterRotation.
    const HOLD_MS = 20000;
    const FADE_MS = 280;
    starterRotation.timeoutId = setTimeout(() => {
      if (!starterRotation.active) return;
      const chips = container.querySelectorAll('.starter-chip');
      chips.forEach((c, idx) => {
        setTimeout(() => {
          (c as HTMLElement).style.opacity = '0';
          (c as HTMLElement).style.transform = 'translateY(-4px)';
        }, idx * 60);
      });
      starterRotation.timeoutId = setTimeout(() => {
        if (!starterRotation.active) return;
        starterRotation.round += 1;
        renderStarterChips(pickStartersFresh(4));
      }, FADE_MS);
    }, lastFinishAt + HOLD_MS);
  }

  function bindEvents() {
    const form = document.getElementById('form') as HTMLFormElement | null;
    const input = document.getElementById('input') as HTMLTextAreaElement | null;
    const clearBtn = document.querySelector('[data-action="clear"]') as HTMLButtonElement | null;

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (state.busy) return;
      const text = input?.value.trim() ?? '';
      if (!text) return;
      if (input) input.value = '';
      await sendMessage(text);
    });

    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        form?.requestSubmit();
      }
    });
    // Typing in the input cancels chip rotation. Do NOT use 'focus' here -
    // we call focusInput() programmatically after every render and a focus
    // listener would kill the rotation before it ever ran a cycle.
    input?.addEventListener('input', stopStarterRotation, { once: true });

    clearBtn?.addEventListener('click', () => {
      if (!confirm('Clear this conversation?')) return;
      track('chat_cleared', { conversation_id: state.conversationId ?? 'unknown' });
      state.messages = [];
      state.conversationId = undefined;
      clearConversation();
      render();
    });

    // Delegate clicks on every external link inside the widget.
    root.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement | null)?.closest(
        'a.btn-cta, a.banner-cta, .msg-cta a, a.brand',
      ) as HTMLAnchorElement | null;
      if (!target) return;
      // Middle / cmd / ctrl click: let the browser do its native thing.
      const me = e as MouseEvent;
      if (me.button !== 0 || me.metaKey || me.ctrlKey || me.shiftKey) return;
      e.preventDefault();
      const url = target.href;
      const isBooking = target.classList.contains('btn-cta')
        || target.classList.contains('banner-cta')
        || target.closest('.msg-cta');
      if (isBooking) {
        track('chat_booking_clicked', {
          location: target.classList.contains('btn-cta') ? 'header'
            : target.classList.contains('banner-cta') ? 'banner'
            : 'inline',
          conversation_id: state.conversationId ?? 'none',
        });
      }
      openExternal(url);
    });
  }

  function focusInput() {
    const input = document.getElementById('input') as HTMLTextAreaElement | null;
    input?.focus();
  }

  async function sendMessage(text: string): Promise<void> {
    state.busy = true;
    const userMsg: StoredMessage = { role: 'user', content: text, timestamp: Date.now() };
    state.messages.push(userMsg);
    persist();
    renderMessages();
    setBusy(true);

    track('chat_message_sent', {
      length: text.length,
      message_index: state.messages.filter((m) => m.role === 'user').length,
      conversation_id: state.conversationId ?? 'new',
    });

    try {
      const r = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: state.messages.map((m) => ({ role: m.role, content: m.content })),
          conversationId: state.conversationId,
        }),
      });

      let replyText: string;
      if (!r.ok) {
        const err = (await r.json().catch(() => ({}))) as { reply?: string; error?: string };
        replyText =
          err.reply ??
          'Something went wrong on our side. Please try again, or book an intro call directly.';
        track('chat_error', {
          status: r.status,
          error: err.error ?? 'unknown',
          conversation_id: state.conversationId ?? 'unknown',
        });
      } else {
        const data = (await r.json()) as {
          reply: string;
          conversationId?: string;
          usage?: {
            inputTokens: number;
            outputTokens: number;
            cacheReadInputTokens: number;
            cacheCreationInputTokens: number;
          };
        };
        replyText = data.reply;
        // CRITICAL: persist the conversationId from the first response so
        // every subsequent request joins the same Firestore conversation.
        if (data.conversationId && !state.conversationId) {
          state.conversationId = data.conversationId;
        }
        track('chat_message_received', {
          length: replyText.length,
          conversation_id: state.conversationId ?? 'unknown',
          input_tokens: data.usage?.inputTokens ?? 0,
          output_tokens: data.usage?.outputTokens ?? 0,
          cache_read_tokens: data.usage?.cacheReadInputTokens ?? 0,
        });
      }

      state.messages.push({ role: 'assistant', content: replyText, timestamp: Date.now() });
      persist();
    } catch (e) {
      state.messages.push({
        role: 'assistant',
        content: 'Network error. Please try again, or book an intro call directly.',
        timestamp: Date.now(),
      });
      persist();
      track('chat_error', {
        status: 0,
        error: 'network',
        message: e instanceof Error ? e.message : 'unknown',
        conversation_id: state.conversationId ?? 'unknown',
      });
    } finally {
      state.busy = false;
      setBusy(false);
      renderMessages();
      focusInput();
    }
  }

  function setBusy(busy: boolean) {
    const send = document.getElementById('send') as HTMLButtonElement | null;
    if (send) send.disabled = busy;
    const input = document.getElementById('input') as HTMLTextAreaElement | null;
    if (input) input.disabled = busy;
    const messages = document.getElementById('messages');
    if (busy && messages) {
      messages.insertAdjacentHTML(
        'beforeend',
        `<div class="msg msg-assistant msg-thinking" id="thinking"><div class="msg-bubble"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div></div>`,
      );
      messages.scrollTop = messages.scrollHeight;
    }
  }

  render();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
